import { useState } from 'react';
import {
  DndContext,
  DragOverlay,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import type { DragEndEvent, DragStartEvent } from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { GripVertical, Plus, Trash2 } from 'lucide-react';
import type { Component, Page } from '../ComponentEditModal.types';
import type { NavigationMenuChildLink } from '../../../schemas/validation';
import { PageSelector } from '../shared/PageSelector';
import { menuLinkUpdateForUrl } from '../../../utils/menuLinkLabel';
import { subLinkDragId } from './reorderSubLinks';

interface NavigationMenuSubLinksProps {
  parentLabel: string;
  links: NavigationMenuChildLink[];
  pages: Page[];
  isLoadingPages: boolean;
  currentPageComponents?: Component[];
  canAdd: boolean;
  onAdd: () => void;
  onUpdate: (childIndex: number, updates: Partial<NavigationMenuChildLink>) => void;
  onRemove: (childIndex: number) => void;
  onReorder: (activeId: string, overId: string) => void;
}

export const NavigationMenuSubLinks: React.FC<NavigationMenuSubLinksProps> = ({
  parentLabel,
  links,
  pages,
  isLoadingPages,
  currentPageComponents,
  canAdd,
  onAdd,
  onUpdate,
  onRemove,
  onReorder,
}) => {
  const [activeId, setActiveId] = useState<string | null>(null);
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );
  const dragIds = links.map((child, index) => subLinkDragId(child, index));
  const activeChild = activeId
    ? links.find((child, index) => subLinkDragId(child, index) === activeId)
    : null;

  const handleDragStart = (event: DragStartEvent) => {
    setActiveId(String(event.active.id));
  };

  const handleDragEnd = (event: DragEndEvent) => {
    setActiveId(null);
    const overId = event.over?.id;
    if (overId == null) return;
    onReorder(String(event.active.id), String(overId));
  };

  return (
    <div className="ml-4 space-y-2 border-l border-gray-200 pl-3">
      <p className="text-xs font-medium text-gray-500">Dropdown links</p>
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
        onDragCancel={() => setActiveId(null)}
      >
        <SortableContext items={dragIds} strategy={verticalListSortingStrategy}>
          <div className="space-y-2">
            {links.map((child, childIndex) => (
              <SortableSubLinkRow
                key={subLinkDragId(child, childIndex)}
                child={child}
                childIndex={childIndex}
                dragId={dragIds[childIndex]}
                parentLabel={parentLabel}
                pages={pages}
                isLoadingPages={isLoadingPages}
                currentPageComponents={currentPageComponents}
                onUpdate={onUpdate}
                onRemove={onRemove}
              />
            ))}
          </div>
        </SortableContext>
        <DragOverlay>
          {activeChild ? (
            <div className="flex items-center gap-2 rounded-md border border-gray-200 bg-white px-3 py-2 text-sm shadow-lg">
              <GripVertical size={16} />
              <span>{activeChild.text}</span>
            </div>
          ) : null}
        </DragOverlay>
      </DndContext>
      <button
        type="button"
        onClick={onAdd}
        disabled={!canAdd}
        className="w-full px-3 py-2 border border-dashed border-gray-300 rounded-md text-xs text-gray-600 hover:border-primary-400 hover:text-primary-600 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-1"
      >
        <Plus size={14} />
        <span>Add sub link</span>
      </button>
    </div>
  );
};

interface SortableSubLinkRowProps {
  child: NavigationMenuChildLink;
  childIndex: number;
  dragId: string;
  parentLabel: string;
  pages: Page[];
  isLoadingPages: boolean;
  currentPageComponents?: Component[];
  onUpdate: (childIndex: number, updates: Partial<NavigationMenuChildLink>) => void;
  onRemove: (childIndex: number) => void;
}

const SortableSubLinkRow: React.FC<SortableSubLinkRowProps> = ({
  child,
  childIndex,
  dragId,
  parentLabel,
  pages,
  isLoadingPages,
  currentPageComponents,
  onUpdate,
  onRemove,
}) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: dragId,
  });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`flex space-x-2 items-center ${isDragging ? 'opacity-40' : ''}`}
    >
      <button
        type="button"
        className="p-2 text-gray-400 hover:text-gray-600 cursor-grab"
        {...attributes}
        {...listeners}
        aria-label={`Reorder ${child.text}`}
      >
        <GripVertical size={16} />
      </button>
      <input
        type="text"
        value={child.text}
        onChange={event => onUpdate(childIndex, { text: event.target.value })}
        className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
        placeholder="Sub link text"
        aria-label={`${parentLabel} sub link ${childIndex + 1} text`}
      />
      <PageSelector
        value={child.url}
        onChange={url =>
          onUpdate(childIndex, menuLinkUpdateForUrl(child.text, child.url, url, pages))
        }
        pages={pages}
        isLoadingPages={isLoadingPages}
        currentPageComponents={currentPageComponents}
      />
      <input
        type="number"
        value={child.order ?? childIndex}
        onChange={event => {
          const numValue = event.target.value === '' ? 0 : parseInt(event.target.value, 10);
          onUpdate(childIndex, { order: !isNaN(numValue) ? numValue : 0 });
        }}
        className="w-16 px-2 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
        aria-label={`${parentLabel} sub link ${childIndex + 1} order`}
        min="0"
      />
      <button
        type="button"
        onClick={() => onRemove(childIndex)}
        className="p-2 text-red-600 hover:text-red-700"
        aria-label={`Remove ${child.text}`}
      >
        <Trash2 size={16} />
      </button>
    </div>
  );
};
